import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  OnInit,
  ViewContainerRef,
  computed,
  viewChild,
  effect,
  linkedSignal,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  GlobalStore,
  SessionStorageService,
} from '@maximizer/core/shared/data-access';
import { CDN_URL, LeadStatus, UserRights } from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import { DisposableComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  EmailSubscription,
  EmailSyncService,
} from '@maximizer/outlook-sync/data-access';
import { EmailDetailComponent } from '@maximizer/outlook/interaction-log/ui';
import {
  EntrySearchService,
  RelatedService,
} from '@maximizer/outlook/main/data-access';
import { StepGuideComponent } from '@maximizer/outlook/onboarding/ui';
import {
  AbEntryService,
  EmailService,
  LeadService,
  OnboardingService,
  OnboardingStepNumber,
  OnboardingStore,
  OutlookService,
  OutlookStore,
  StepGuideId,
  TrackerService,
  getPermissionString,
} from '@maximizer/outlook/shared/data-access';
import {
  AbEntryLastContactDatePreference,
  AbEntryType,
  EmailEntryType,
  EntrySearchResult,
  EntryType,
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
  OutlookAbEntryDetails,
  OutlookEmail,
  OutlookEmailDetail,
  OutlookLeadSearch,
} from '@maximizer/outlook/shared/domain';
import {
  MenuItem,
  ONBOARDING_GUIDE_OFFSETS,
  ONBOARDING_INITIALIZE_DELAY,
  ONBOARDING_STEP_POPOVER_OPEN_DELAY,
  ONBOARDING_STEP_POPOVER_WIDTH,
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { OutlookEntrySearchComponent } from '../../entry-search/entry-search.component';
import { OutlookReadEntryRowComponent } from './entry-row/entry-row.component';
import { OutlookInMaximizerListComponent } from './in-maximizer-list/in-maximizer-list.component';
import {
  AddEntryEvent,
  OutlookNotInMaximizerListComponent,
} from './not-in-maximizer-list/not-in-maximizer-list.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import {
  KENDO_LISTVIEW,
  PagerSettings,
} from '@progress/kendo-angular-listview';
import { PageChangeEvent } from '@progress/kendo-angular-pager';
import {
  KENDO_TOOLTIPS,
  PopoverContainerDirective,
  Position,
} from '@progress/kendo-angular-tooltip';
import { DataResult, State, process } from '@progress/kendo-data-query';
import {
  Observable,
  catchError,
  concatMap,
  finalize,
  forkJoin,
  from,
  map,
  of,
} from 'rxjs';

@Component({
  selector: 'maximizer-outlook-read',
  templateUrl: './read.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    EmailDetailComponent,
    KENDO_DIALOG,
    KENDO_LISTVIEW,
    KENDO_TOOLTIPS,
    OUTLOOK_SHARED_UI,
    OutlookEntrySearchComponent,
    OutlookInMaximizerListComponent,
    OutlookNotInMaximizerListComponent,
    OutlookReadEntryRowComponent,
    StepGuideComponent,
    TranslatePipe,
    UI_LOADER,
  ],
})
export class OutlookMailReadComponent
  extends DisposableComponent
  implements OnInit, AfterViewInit
{
  @HostBinding('class.max-outlook') hostClass = true;

  notification =
    viewChild.required<OutlookNotificationComponent>('notification');
  readContainer = viewChild.required<ElementRef>('readContainer');
  entrySearch = viewChild.required(OutlookEntrySearchComponent);

  // Onboarding
  addContact1Container = viewChild<PopoverContainerDirective>(
    'addContact1Container',
  );
  addContact1Anchor = viewChild<Element>('addContact1Anchor');
  saveEmail1Container = viewChild<PopoverContainerDirective>(
    'saveEmail1Container',
  );
  saveEmail1Anchor = viewChild<Element>('saveEmail1Anchor');
  saveEmail2Container = viewChild<PopoverContainerDirective>(
    'saveEmail2Container',
  );
  saveEmail2Anchor = viewChild.required<Element>('saveEmail2Anchor');
  entryActions1Anchor = viewChild<Element>('entryActions1Anchor');
  entryActions1Container = viewChild<PopoverContainerDirective>(
    'entryActions1Container',
  );

  get inMaximizerDataResult(): DataResult {
    return process(this.inMaximizer, this.state);
  }

  get notInMaximizerDataResult(): DataResult {
    return process(this.notInMaximizer, this.state);
  }

  isSavingFromPopup = false;
  EntryType = EntryType;
  newEntries: string[] = [];
  pageTitle = '';
  deepLink = '';
  resourceName = '';
  disableSaveEmailAllEntries = false;
  outlookEmails: OutlookEmail[] = [];
  notInMaximizer: OutlookEmail[] = [];
  inMaximizer: OutlookEmail[] = [];
  otherInMaximizer: OutlookEmail[] = [];
  hasAbEntries?: boolean;
  hasLeads?: boolean;
  relatedTitle = '';
  showRelatedSection = true;
  userRights?: UserRights;
  saveEmailDetail = false;
  saveEmailDetailProperties?: OutlookEmailDetail;
  autoSaveEnabledList: string[] = [];
  popoverPosition: Position = 'bottom';
  onboardingInitialized = false;
  trackingEventLocation = EventModule.Received;
  saveToAll = false;

  private onboardingTimeoutId: ReturnType<typeof setTimeout> | null = null;

  readonly StepGuideId = StepGuideId;
  readonly OnboardingStepNumber = OnboardingStepNumber;
  readonly OnboardingGuideOffsets = ONBOARDING_GUIDE_OFFSETS;
  readonly stepPopoverWidth = ONBOARDING_STEP_POPOVER_WIDTH;

  searchActive = signal(false);
  entryActionsGuideVisible = signal(false);
  onboardingGuide = computed(() => this.onboardingStore.inProgress());

  step2Guide2Visible = linkedSignal(
    computed(() => {
      if (this.searchActive()) {
        return false;
      }
      return (
        this.onboardingGuide() &&
        this.onboardingStore.currentGuide() === StepGuideId.SaveEmail2
      );
    }),
  );

  buildSaveEmailActions = (entry: EntrySearchResult): MenuItem[] => {
    const alreadySaved = this.isEmailSavedToEntry(entry.id);
    const hasPermission = this.getSaveEmailPermission(entry.type);

    let icon: string;
    let title: string | undefined;

    if (alreadySaved) {
      icon = 'maximizer-icons icon-saved-email text-neutral-40 icon-20';
      title = this.translate.instant('outlook.main.tooltip.email-saved');
    } else if (hasPermission) {
      icon = 'maximizer-icons icon-save-email icon-24';
      title = undefined;
    } else {
      icon = 'maximizer-icons icon-save-email text-neutral-40 icon-24';
      title = this.getSaveEmailPermissionMessage(entry.type);
    }

    return [
      {
        id: 'save-email',
        text: this.translate.instant('outlook.buttons.saveEmail'),
        icon,
        disabled: alreadySaved || !hasPermission,
        title,
        click: () => this.saveEmailFromSearch(entry),
      },
    ];
  };

  loading = {
    skeleton: true,
    saveEmail: false,
    syncSubscriptions: false,
  };

  pageSize = 4;
  pagerSettingsNotInMax: PagerSettings | boolean = false;
  pagerSettingsInMax: PagerSettings | boolean = false;
  defaultPagerSettings: PagerSettings = {
    position: 'bottom',
    pageSizeValues: false,
    info: false,
    previousNext: true,
    type: 'numeric',
    buttonCount: 4,
  };

  state: State = {
    skip: 0,
    take: this.pageSize,
  };

  addEntryMenuItems: MenuItem[] = [
    {
      id: 'individual',
      text: 'outlook.main.actions.addIndividual',
      route: '/abentry/add/individual',
      icon: 'fa-icons icon-regular icon-18 icon-building-user',
      disabled: false,
      title: 'outlook.entries.individuals',
    },
    {
      id: 'contact',
      text: 'outlook.main.actions.addContact',
      route: '/abentry/add/contact',
      icon: 'fa-icons icon-solid icon-18 icon-user',
      disabled: false,
      title: 'outlook.entries.contacts',
    },
    {
      id: 'lead',
      text: 'outlook.main.actions.addLead',
      route: '/lead/add',
      icon: 'fa-icons icon-solid icon-18 icon-user-magnifying-glass',
      disabled: false,
      title: 'outlook.entries.leads',
    },
  ];

  constructor(
    @Inject(CDN_URL) public cdn: string,
    private readonly entrySearchService: EntrySearchService,
    private readonly leadService: LeadService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly abentryService: AbEntryService,
    private readonly storage: SessionStorageService,
    private readonly outlookService: OutlookService,
    private readonly outlookSyncService: EmailSyncService,
    private readonly insightsService: InsightsService,
    private readonly tracker: TrackerService,
    private readonly emailService: EmailService,
    private readonly relatedService: RelatedService,
    public globalStore: GlobalStore,
    public outlookStore: OutlookStore,
    public onboardingStore: OnboardingStore,
    public onboardingService: OnboardingService,
    public viewContainerRef: ViewContainerRef,
  ) {
    super();

    // Show or hide guides if onboarding in progress changes
    effect(() => {
      if (this.onboardingGuide()) {
        this.showOnboardingGuide(this.onboardingStore.currentGuide());
      } else {
        this.hideOnboardingGuide(this.onboardingStore.currentGuide());
      }
    });

    // Hide guide when search popup active to avoid overlap
    effect(() => {
      const shouldShow = this.step2Guide2Visible();
      if (shouldShow && this.saveEmail2Container()) {
        this.saveEmail2Container()?.show(this.saveEmail2Anchor());
      } else if (!shouldShow && this.saveEmail2Container()) {
        this.saveEmail2Container()?.hide();
      }
    });
  }

  ngOnInit(): void {
    this.router.events.subscribe((event: unknown) => {
      if (!(event instanceof NavigationEnd)) return;
      if (event.urlAfterRedirects.startsWith('/read')) {
        const newItems = this.storage.get(
          this.outlookService.storagePartitionKey + 'new-item',
        );
        if (!newItems) return;
        const newEntriesArray = JSON.parse(newItems);
        if (this.newEntries.length !== newEntriesArray?.length) {
          const emails = this.outlookService.getReadEmails();
          this.initialize(emails);
        }
      }
    });

    this.outlookService.updateEmails().then(() => {
      const emails = this.outlookService.getReadEmails();
      this.initialize(emails);
    });

    this.outlookService?.mailbox?.addHandlerAsync(
      Office.EventType.ItemChanged,
      this.onboardingService.reset.bind(this.onboardingService),
    );

    setTimeout(() => {
      if (
        this.outlookStore.hasActiveAccount() &&
        this.outlookStore.doesGraphAccountMatchInbox()
      ) {
        this.onboardingService.getSettings();
      }
    }, ONBOARDING_INITIALIZE_DELAY);

    this.setUserRights();
    this.outlookStore.loadUsers();
  }

  ngAfterViewInit(): void {
    if (this.onboardingStore.inProgress() && !this.onboardingInitialized) {
      this.onboardingInitialized = true;
      const waitAndInitialize = () => {
        if (this.loading.skeleton) {
          setTimeout(() => {
            waitAndInitialize();
          }, ONBOARDING_INITIALIZE_DELAY);
        } else {
          this.initializeOnboardingState(this.onboardingStore.currentStep());
        }
      };
      setTimeout(() => {
        waitAndInitialize();
      }, ONBOARDING_STEP_POPOVER_OPEN_DELAY);
    }
  }

  setUserRights(): void {
    if (this.globalStore.session()?.user) {
      this.userRights = this.globalStore.session()?.user.rights;

      if (!this.userRights?.lead?.insert) {
        const leadItem = this.addEntryMenuItems.find(
          (item) => item.id === EntryType.Lead,
        );
        if (leadItem) {
          leadItem.disabled = true;
        }
      }

      if (!this.userRights?.addressBook?.insert) {
        this.addEntryMenuItems.forEach((item) => {
          if (
            item.id === EntryType.Individual ||
            item.id === EntryType.Contact
          ) {
            item.disabled = true;
          }
        });
      }

      this.showRelatedSection = this.relatedService.canShowRelatedSection(
        this.userRights,
      );
      if (this.showRelatedSection) {
        this.relatedTitle = this.relatedService.getRelatedTitle(
          this.userRights,
        );
      }
    }
  }

  initialize(emails: OutlookEmail[]): void {
    if (emails.length === 0) {
      this.loading.skeleton = false;
      return;
    }

    const lowerCaseEmails = emails.map((o) => {
      o.emailAddress = o.emailAddress.toLocaleLowerCase();
      return o;
    });
    this.outlookEmails = [...lowerCaseEmails];

    this.loadEmailsInMaximizer();
    this.loadSavedToOthersInMaximizer();
    this.checkSyncHealthIfStale();
  }

  private checkSyncHealthIfStale(): void {
    const config = this.outlookStore.syncConfiguration();
    if (!config?.id || !config.enabled) return;

    const lastChecked = this.outlookStore.syncHealthLastChecked();
    const SYNC_HEALTH_CHECK_INTERVAL = 5 * 60 * 1000;
    const isStale =
      lastChecked === null ||
      Date.now() - lastChecked > SYNC_HEALTH_CHECK_INTERVAL;

    if (isStale) {
      this.outlookStore.requestSyncHealthCheck();
    }
  }

  resetAllProps(): void {
    this.loading = {
      skeleton: true,
      saveEmail: false,
      syncSubscriptions: true,
    };

    this.disableSaveEmailAllEntries = false;
    this.notInMaximizer = [];
    this.inMaximizer = [];
    this.otherInMaximizer = [];
    this.pagerSettingsInMax = false;
    this.pagerSettingsNotInMax = false;
  }

  async loadSavedToOthersInMaximizer(): Promise<void> {
    const entries = await this.outlookService.getSavedEmails();
    if (!entries?.othersInMaximizer) return;

    const { leads, abEntries } = this.categorizeEntries(
      entries.othersInMaximizer,
    );

    const requests: {
      abentries?: Observable<OutlookAbEntryDetails[]>;
      leads?: Observable<OutlookLeadSearch[]>;
    } = {};

    if (this.userRights?.addressBook.read && abEntries.length > 0) {
      requests.abentries = this.abentryService.getByKeys(abEntries);
    } else {
      requests.abentries = of([]);
    }

    if (this.userRights?.lead.read && leads.length > 0) {
      requests.leads = this.leadService.getKeys(leads);
    } else {
      requests.leads = of([]);
    }

    forkJoin(requests)
      .pipe(catchError(async (error) => this.showLoadError(error)))
      .subscribe((data) => {
        const fetchedAbEntries = data.abentries || [];
        const fetchedLeads = data.leads || [];

        if (fetchedAbEntries.length === 0 && fetchedLeads.length === 0) return;

        this.populateOthersInMaximizer(fetchedAbEntries, fetchedLeads);
      });
  }

  private categorizeEntries(entries: { id?: string }[]): {
    leads: string[];
    abEntries: string[];
  } {
    const leads: string[] = [];
    const abEntries: string[] = [];

    for (const entry of entries) {
      const id = entry.id ?? '';
      try {
        const decoded = atob(id);
        const firstToken = decoded.split('\t')[0];
        if (firstToken.toLowerCase() === EntryType.Lead) {
          leads.push(id);
        } else {
          abEntries.push(id);
        }
      } catch (error) {
        this.handleError(error, `Failed to decode base64 ID: "${id}"`);
      }
    }

    return { leads, abEntries };
  }

  private populateOthersInMaximizer(
    abentries: OutlookAbEntryDetails[],
    leads: OutlookLeadSearch[],
  ): void {
    const maxAbentries = this.mapAbEntriesToOutlookEmail(abentries);
    maxAbentries.forEach((o) => {
      o.emailSaved = true;
    });

    const maxLeads = this.mapLeadsToOutlookEmail(leads);
    maxLeads.forEach((o) => {
      o.emailSaved = true;
    });

    const allEntries = [...maxAbentries, ...maxLeads];

    // Prefixed `index` property for unique Google Tag Manager id
    const taggedEntries = allEntries.map((entry) => ({
      ...entry,
      tag: `other-${entry.id}`,
    }));

    this.otherInMaximizer = taggedEntries;
  }

  loadEmailsInMaximizer(): void {
    const emails = this.outlookEmails.map((o) => o.emailAddress);
    if (emails.length === 0) {
      this.loading.skeleton = false;
      return;
    }
    this.loading.skeleton = true;

    const requests: {
      abentries?: Observable<OutlookAbEntryDetails[]>;
      leads?: Observable<OutlookLeadSearch[]>;
    } = {
      abentries: this.abentryService.getByEmails(emails),
    };

    if (this.userRights?.lead.read) {
      requests.leads = this.leadService.getByEmails(emails);
    }

    forkJoin(requests)
      .pipe(
        catchError(async (error) => this.showLoadError(error)),
        finalize(() => {
          this.mapNotInMaximizer();
          this.loading.skeleton = false;
        }),
      )
      .subscribe(async (data) => {
        const abEntries = data.abentries || [];
        const leads = data.leads || [];

        if (abEntries.length === 0 && leads.length === 0) {
          return;
        }

        await this.populateInMaximizer(emails, { abentries: abEntries, leads });
      });
  }

  private async populateInMaximizer(
    emails: string[],
    data: { abentries: OutlookAbEntryDetails[]; leads: OutlookLeadSearch[] },
  ): Promise<void> {
    this.mapAbEntryKeyAndParentKey(data.abentries);

    const abEntries: OutlookEmail[] = this.mapAbEntriesToOutlookEmail(
      data.abentries,
    );
    const leads: OutlookEmail[] = this.mapLeadsToOutlookEmail(data.leads);

    let allContacts: OutlookEmail[] = [...abEntries];
    if (leads.length > 0) {
      allContacts = allContacts.concat(leads);
    }

    if (allContacts.length > 4) {
      this.pagerSettingsInMax = { ...this.defaultPagerSettings };
    }

    allContacts.sort(
      (a, b) => emails.indexOf(a.emailAddress) - emails.indexOf(b.emailAddress),
    );

    // Prefixed `tag` property for unique Google Tag Manager id
    allContacts = allContacts.map((entry) => ({
      ...entry,
      tag: `inMaximizer-${entry.emailAddress}`,
    }));

    this.mapEntriesWithNew(allContacts);
    this.getSavedEmails();
    this.inMaximizer = [...allContacts];
    this.populateAutoSaveStatusForCurrentPage();
  }

  mapAbEntryKeyAndParentKey(abEntries: OutlookAbEntryDetails[]): void {
    if (abEntries.length === 0) {
      this.outlookStore.updateInMaximizerIdList([]);
      return;
    }

    const abEntryKeys = abEntries
      .map((entry) => entry.key ?? '')
      .filter((key) => key !== '');

    const abEntryParentKeys = abEntries
      .map((entry) => entry.parentKey ?? '')
      .filter((key) => key !== '');

    this.outlookStore.updateInMaximizerIdList([
      ...abEntryKeys,
      ...abEntryParentKeys,
    ]);
  }

  navigateToRelated(): void {
    this.relatedService.navigateToRelated();
  }

  private async getSavedEmails(): Promise<void> {
    const allEmails = await this.outlookService.getSavedEmails();

    for (const entryFromOutlook of allEmails.inMaximizer) {
      const entryFromMaximizerIndex = this.inMaximizer.findIndex(
        (o) => o.id === entryFromOutlook.id,
      );
      if (entryFromMaximizerIndex === -1) continue;
      this.inMaximizer[entryFromMaximizerIndex].emailSaved = true;
    }

    if (this.inMaximizer.every((o) => o.emailSaved)) {
      this.disableSaveEmailAllEntries = true;
    }
  }

  navigateToAdd(event: { route: string }, email: string, name: string): void {
    this.closeTooltips();

    // Prevent email address population on form name field - clear if they match
    const cleanedName = email === name ? '' : name;
    this.router.navigate([event.route], {
      queryParams: { email, name: cleanedName },
    });
  }

  navigateToEntry(id: string, type: string): void {
    this.closeTooltips();
    this.router.navigate(['/' + type + '/' + id]);
  }

  saveEmail(entry: OutlookEmail, fromPopup: boolean): void {
    this.isSavingFromPopup = fromPopup;
    if (this.outlookStore.preferences().saveEmailDetail) {
      this.saveToAll = false;
      this.openSaveEmailDetails(entry);
    } else {
      this.performSaveEmail(entry, this.isSavingFromPopup);
    }
  }

  saveEmailForAllEntries(): void {
    const unsavedEntries = this.inMaximizer.filter(
      (entry) => !entry.emailSaved,
    );

    if (unsavedEntries.length === 0) {
      return;
    }

    if (this.outlookStore.preferences().saveEmailDetail) {
      this.saveToAll = true;
      this.openSaveEmailDetails(unsavedEntries);
    } else {
      this.performSaveEmailForAllEntries(unsavedEntries);
    }
  }

  private openSaveEmailDetails(entry: OutlookEmail | OutlookEmail[]): void {
    const item = this.outlookService.mailboxItem;

    this.saveEmailDetail = true;
    this.saveEmailDetailProperties = {
      subject: item?.subject ?? '',
      entry: Array.isArray(entry) ? entry : [entry],
      abentryType: Array.isArray(entry) ? undefined : entry.abentryType,
    };
  }

  private async performSaveEmail(
    entry: OutlookEmail,
    fromPopup: boolean,
  ): Promise<void> {
    if (!entry.id) return;

    const entryId = entry.id;
    const isPopup = fromPopup;
    const index = this.inMaximizer.findIndex((entry) => entry.id === entryId);
    const existing = index === -1 ? undefined : this.inMaximizer[index];

    this.loading.saveEmail = true;
    let isSuccessful = false;

    try {
      const emlFile = await this.outlookService.getReadEmlFile();
      if (!emlFile) {
        this.showSaveEmailErrorNotification(false);
        return;
      }
      const documentId = await this.emailService.saveEmailToSingleEntry(
        emlFile,
        entryId,
      );
      if (!documentId) {
        this.showSaveEmailErrorNotification(false);
        return;
      }

      if (
        entry.type === EntryType.AbEntry &&
        this.outlookStore.isSentFolder()
      ) {
        this.handleLastContactDate(entry);
      }

      entry.emailSaved = true;
      this.showSaveEmailSuccessNotification(false);
      isSuccessful = true;

      if (existing) {
        await this.handleExistingEntry(existing, index, documentId);
      } else {
        await this.handleNewEntry(entry, documentId, isPopup);
      }

      if (isPopup) {
        this.entrySearch().refreshActions();
      }
    } catch (error) {
      this.handleError(error, 'Error saving email');
      this.showSaveEmailErrorNotification(false);
    } finally {
      this.loading.saveEmail = false;

      if (!isPopup && this.onboardingStore.inProgress()) {
        this.nextOnboardingGuide(StepGuideId.SaveEmail1);
      }

      this.trackEmailSaveResult(isSuccessful, entry);
    }
  }

  private async handleExistingEntry(
    existing: OutlookEmail,
    index: number,
    documentId: string,
  ): Promise<void> {
    existing.emailSaved = true;
    existing.emailKey = documentId;
    this.inMaximizer = [...this.inMaximizer];

    const page = Math.floor(index / this.pageSize);
    if (!(this.state.skip === 0 && page === 0)) {
      this.state.skip = page * this.pageSize;
    }

    await this.outlookService.saveKeysInOutlook([existing], false);
  }

  private async handleNewEntry(
    entry: OutlookEmail,
    documentId: string,
    isPopup: boolean,
  ): Promise<void> {
    entry.emailKey = documentId;

    if (isPopup) {
      const newEntry = {
        ...entry,
        emailSaved: true,
        emailAddress: entry.emailAddress ?? '',
      };

      this.otherInMaximizer.push(newEntry);
      this.otherInMaximizer = [...this.otherInMaximizer];

      try {
        await this.outlookService.saveKeysInOutlook([newEntry], isPopup);
      } catch (error) {
        this.handleError(error, 'Error saving popup entry to Outlook storage');
      }
    } else {
      try {
        await this.outlookService.saveKeysInOutlook([entry], isPopup);
      } catch (error) {
        this.handleError(error, 'Error saving entry to Outlook storage');
      }
    }
  }

  async performSaveEmailForAllEntries(entries: OutlookEmail[]): Promise<void> {
    let isSuccessful = false;

    try {
      this.loading.saveEmail = true;

      const emlFile = await this.outlookService.getReadEmlFile();
      if (!emlFile) throw new Error('Unable to retrieve EML file');

      const parentKeys = entries
        .map((entry) => entry.id)
        .filter((id): id is string => !!id);

      if (parentKeys.length === 0) {
        this.showSaveEmailErrorNotification(true);
        return;
      }

      const documentIds = await this.emailService.saveEmailToMultipleEntries(
        emlFile,
        parentKeys,
      );

      isSuccessful = documentIds?.length === parentKeys.length;

      if (documentIds?.length) {
        this.inMaximizer
          .filter((entry) => entry.id && parentKeys.includes(entry.id))
          .forEach((entry) => {
            entry.emailSaved = true;
          });
        this.inMaximizer = [...this.inMaximizer];
      }

      if (isSuccessful && this.outlookStore.isSentFolder()) {
        entries
          .filter((entry) => entry.type === EntryType.AbEntry && entry.id)
          .forEach((entry) => this.handleLastContactDate(entry));
      }

      if (isSuccessful) {
        this.showSaveEmailSuccessNotification(true);
        this.disableSaveEmailAllEntries = true;
      } else {
        this.showSaveEmailErrorNotification(true);
      }

      await this.outlookService.saveKeysInOutlook(this.inMaximizer, false);
    } catch {
      this.showSaveEmailErrorNotification(true);
    } finally {
      this.loading.saveEmail = false;

      if (this.onboardingStore.inProgress()) {
        this.nextOnboardingGuide(StepGuideId.SaveEmail1);
      }

      this.tracker.trackEvent({
        module: EventModule.Email,
        journey: EventJourney.SaveToAll,
        action: EventAction.Completed,
        metadata: {
          result: isSuccessful ? EventMetadata.Success : EventMetadata.Failed,
          emailDetails: false,
        },
      });
    }
  }

  onSaveEmailDetail(updatedEntries: OutlookEmail[]): void {
    const fromPopup = this.isSavingFromPopup;
    const multipleEntries = updatedEntries.length > 1;

    this.isSavingFromPopup = false;
    this.saveEmailDetail = false;
    this.saveEmailDetailProperties = undefined;

    updatedEntries.forEach((updatedEntry) => {
      const index = this.inMaximizer.findIndex(
        (entry) => entry.id === updatedEntry.id,
      );

      if (index !== -1) {
        this.inMaximizer[index] = {
          ...this.inMaximizer[index],
          emailSaved: true,
          emailKey: updatedEntry.emailKey,
        };
      } else if (fromPopup) {
        const newEntry = {
          ...updatedEntry,
          emailSaved: true,
          emailAddress: updatedEntry.emailAddress ?? '',
        };
        this.otherInMaximizer.push(newEntry);
      }
    });

    this.inMaximizer = [...this.inMaximizer];
    this.otherInMaximizer = [...this.otherInMaximizer];
    this.loading.saveEmail = false;

    if (fromPopup) {
      this.entrySearch().refreshActions();
    }

    if (this.outlookStore.isSentFolder()) {
      updatedEntries
        .filter((entry) => entry.type === EntryType.AbEntry && entry.id)
        .forEach((entry) => this.handleLastContactDate(entry));
    }

    if (!fromPopup && this.onboardingStore.inProgress()) {
      this.nextOnboardingGuide(StepGuideId.SaveEmail1);
    }

    const allSaved = this.inMaximizer.every((entry) => entry.emailSaved);
    if (allSaved) {
      this.disableSaveEmailAllEntries = true;
    }

    this.showSaveEmailSuccessNotification(multipleEntries);
    this.outlookService.saveKeysInOutlook(updatedEntries, fromPopup);
  }

  inMaximizerPageChange(event: PageChangeEvent): void {
    this.state.skip = event.skip;
    this.state.take = event.take;

    this.populateAutoSaveStatusForCurrentPage();
  }

  saveEmailFromSearch(entry: EntrySearchResult): void {
    const outlookEmail = this.entrySearchService.toOutlookEmail(entry);
    // Prefixed tag keeps the Google Tag Manager id from the legacy search rows
    outlookEmail.tag = `search-${entry.id}`;
    this.saveEmail(outlookEmail, true);
  }

  private mapEntriesWithNew(localEmails: OutlookEmail[]): void {
    const partitionKey =
      Office.context.partitionKey ??
      Office.context.mailbox?.userProfile.emailAddress ??
      'mx_default';
    const newItem = this.storage.get(partitionKey + 'new-item');
    if (newItem) {
      this.newEntries = JSON.parse(newItem);

      for (const entry of this.newEntries) {
        const index = localEmails.findIndex((o) => o.emailAddress === entry);
        if (index === -1) continue;

        const dataFromOctopus = localEmails[index];
        localEmails.splice(index, 1);
        dataFromOctopus.isNewEntry = true;
        localEmails.unshift(dataFromOctopus);
      }
    }
    this.inMaximizer = [...localEmails];
  }

  private mapNotInMaximizer(): void {
    this.notInMaximizer = this.outlookEmails.filter(
      (o) =>
        !this.inMaximizer.some(
          (inMax) => inMax.emailAddress === o.emailAddress,
        ),
    );
    if (this.notInMaximizer.length > 4) {
      this.pagerSettingsNotInMax = { ...this.defaultPagerSettings };
    }
  }

  private mapAbEntriesToOutlookEmail(
    abentries?: OutlookAbEntryDetails[],
  ): OutlookEmail[] {
    if (!abentries) return [];

    const emails = this.outlookEmails.map((o) => o.emailAddress);

    return (
      abentries.map((ab) => {
        const emailFromOutlook = ab.emails.find((o) =>
          emails.find(
            (e) => e.toLocaleLowerCase() === o.value.toLocaleLowerCase(),
          ),
        );

        const emailAddress =
          emailFromOutlook?.value.toLocaleLowerCase() ??
          (ab.emails.length > 0 ? ab.emails[0].value.toLocaleLowerCase() : '');

        let name = ab.name;
        if (ab.type.toLocaleLowerCase() === EntryType.Company) {
          name = ab.companyName;
        }

        return {
          id: ab.key,
          emailAddress: emailAddress,
          displayName: name,
          type: EntryType.AbEntry,
          abentryType: ab.type.toLocaleLowerCase(),
          parentKey: ab.parentKey,
        } as OutlookEmail;
      }) ?? []
    );
  }

  private mapLeadsToOutlookEmail(lead?: OutlookLeadSearch[]): OutlookEmail[] {
    if (!lead) return [];
    return lead
      .filter((o) => o.status !== LeadStatus.Converted)
      .map((result) => {
        return {
          id: result.key,
          emailAddress: result.email?.toLocaleLowerCase() ?? '',
          displayName: result.name,
          type: EntryType.Lead,
        };
      });
  }

  private showSaveEmailSuccessNotification(isSaveAllEntries: boolean): void {
    const message = isSaveAllEntries
      ? 'outlook.emailNotification.successAllEntries'
      : 'outlook.emailNotification.successEntry';

    this.notification().showMessage('success', message, true);
  }

  private showSaveEmailErrorNotification(isSaveAllEntries: boolean): void {
    const message = isSaveAllEntries
      ? 'outlook.emailNotification.failAllEntries'
      : 'outlook.emailNotification.failEntry';

    this.notification().showMessage('error', message, true);
  }

  private showLoadError(error: unknown): {
    abentries: OutlookAbEntryDetails[];
    leads: OutlookLeadSearch[];
  } {
    this.handleError(error, 'Error loading abentries or leads');
    this.notification().show('load', 'error');
    return { abentries: [], leads: [] };
  }

  checkInMaximizerHasEntriesOrLeads(): void {
    this.hasAbEntries = this.inMaximizer.some(
      (item) => item.type === EntryType.AbEntry,
    );
    this.hasLeads = this.inMaximizer.some(
      (item) => item.type === EntryType.Lead,
    );
  }

  getInMaximizerPermission(): boolean {
    this.checkInMaximizerHasEntriesOrLeads();

    if (
      (this.hasAbEntries && !this.userRights?.addressBook?.update) ||
      (this.hasLeads && !this.userRights?.lead?.update) ||
      !this.userRights?.documents?.insert
    ) {
      return false;
    }
    return true;
  }

  getSaveAllPermissionMessage(): string {
    const getPermission = (
      entryType: EmailEntryType | 'document' | undefined,
      action: 'edit' | 'create' | undefined,
    ) => getPermissionString(this.translate, entryType, action, 'default');

    this.checkInMaximizerHasEntriesOrLeads();
    if (this.hasAbEntries && !this.userRights?.addressBook?.update) {
      return getPermission('abentry', 'edit');
    }
    if (this.hasLeads && !this.userRights?.lead?.update) {
      return getPermission('lead', 'edit');
    }
    if (!this.userRights?.documents?.insert) {
      return getPermission('document', 'create');
    }
    return getPermission(undefined, undefined);
  }

  private isEmailSavedToEntry(entryId: string): boolean {
    return (
      this.otherInMaximizer.some((saved) => saved.id === entryId) ||
      this.inMaximizer.some(
        (existing) => existing.id === entryId && !!existing.emailSaved,
      )
    );
  }

  private getSaveEmailPermission(type?: EmailEntryType): boolean {
    if (!type) {
      return !!this.userRights?.documents?.insert;
    }
    if (
      (type === EntryType.AbEntry && !this.userRights?.addressBook.update) ||
      (type === EntryType.Lead && !this.userRights?.lead.update) ||
      !this.userRights?.documents.insert
    ) {
      return false;
    }
    return true;
  }

  private getSaveEmailPermissionMessage(
    type: EmailEntryType | undefined,
  ): string {
    const getPermission = (
      entryType: EmailEntryType | 'document' | undefined,
      action: 'edit' | 'create' | undefined,
    ) => getPermissionString(this.translate, entryType, action, 'default');

    if (type === EntryType.AbEntry && !this.userRights?.addressBook?.update) {
      return getPermission('abentry', 'edit');
    }
    if (type === EntryType.Lead && !this.userRights?.lead?.update) {
      return getPermission('lead', 'edit');
    }
    if (!this.userRights?.documents?.insert) {
      return getPermission('document', 'create');
    }
    return getPermission(undefined, undefined);
  }

  private scrollToTop(): void {
    if (this.readContainer()) {
      this.readContainer().nativeElement.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  closeAddEntryDropDown() {
    const anchor = this.addContact1Anchor();
    if (
      anchor &&
      this.onboardingStore.inProgress() &&
      this.onboardingStore.currentGuide() === StepGuideId.AddContact1
    ) {
      this.addContact1Container()?.show(anchor);
    }
  }

  openAddEntryDropDown() {
    if (
      this.onboardingStore.inProgress() &&
      this.onboardingStore.currentGuide() === StepGuideId.AddContact1
    ) {
      this.addContact1Container()?.hide();
    }
  }

  // Onboarding
  initializeOnboardingState(currentStep: OnboardingStepNumber): void {
    if (currentStep === OnboardingStepNumber.Step1) {
      this.showOnboardingGuide(StepGuideId.AddContact1);
    } else if (currentStep === OnboardingStepNumber.Step2) {
      this.checkContainerForPopoverPosition();
      this.showOnboardingGuide(StepGuideId.SaveEmail1);
    } else {
      this.checkContainerForPopoverPosition();
      this.showOnboardingGuide(StepGuideId.EntryActions1);
    }
  }

  nextOnboardingGuide(currentGuideId: StepGuideId): void {
    if (currentGuideId === StepGuideId.SaveEmail1) {
      this.checkContainerForPopoverPosition();
      this.hideOnboardingGuide(StepGuideId.SaveEmail1);
      this.showOnboardingGuide(StepGuideId.SaveEmail2);
      this.scrollToTop();
    } else if (currentGuideId === StepGuideId.SaveEmail2) {
      this.nextOnboardingStep(OnboardingStepNumber.Step2);
    }
  }

  nextOnboardingStep(currentStep: OnboardingStepNumber): void {
    this.hideOnboardingGuide(this.onboardingStore.currentGuide());

    if (currentStep === OnboardingStepNumber.Step1) {
      this.onboardingStore.setCurrentStep(OnboardingStepNumber.Step2);
      this.checkContainerForPopoverPosition();
      this.showOnboardingGuide(StepGuideId.SaveEmail1);
    } else if (currentStep === OnboardingStepNumber.Step2) {
      this.onboardingStore.setCurrentStep(OnboardingStepNumber.Step3);
      this.checkContainerForPopoverPosition();
      this.scrollToTop();
      this.showOnboardingGuide(StepGuideId.EntryActions1);
    } else if (currentStep === OnboardingStepNumber.Step3) {
      const firstEntry = this.inMaximizer[0];
      if (firstEntry?.id) {
        this.router.navigate(['/' + firstEntry.type + '/' + firstEntry.id]);
      } else {
        this.onboardingStore.setCurrentStep(OnboardingStepNumber.Step4);
        this.onboardingService.isLastStep.set(true);
        this.onboardingService.showOnboardingDialog.set(true);
      }
    }
  }

  private showOnboardingGuide(guideId: StepGuideId): void {
    this.onboardingStore.setCurrentGuide(guideId);

    if (this.onboardingTimeoutId) {
      clearTimeout(this.onboardingTimeoutId);
      this.onboardingTimeoutId = null;
    }

    this.onboardingTimeoutId = setTimeout(() => {
      const addContact1Anchor = this.addContact1Anchor();
      const saveEmail1Anchor = this.saveEmail1Anchor();
      const entryActions1Anchor = this.entryActions1Anchor();

      switch (guideId) {
        case StepGuideId.AddContact1:
        case StepGuideId.AddContact1Alt:
          if (addContact1Anchor) {
            this.addContact1Container()?.show(addContact1Anchor);
          }
          break;
        case StepGuideId.SaveEmail1:
          if (saveEmail1Anchor) {
            this.saveEmail1Container()?.show(saveEmail1Anchor);
          }
          break;
        case StepGuideId.SaveEmail2:
          // handled by linkedSignal with searchActive
          break;
        case StepGuideId.EntryActions1:
          if (entryActions1Anchor) {
            this.entryActions1Container()?.show(entryActions1Anchor);
            this.entryActionsGuideVisible.set(true);
          }
          break;
        default:
          break;
      }
      this.onboardingTimeoutId = null;
    }, 1000);
  }

  private hideOnboardingGuide(guideId: StepGuideId) {
    if (
      guideId === StepGuideId.AddContact1 ||
      guideId === StepGuideId.AddContact1Alt
    ) {
      this.addContact1Container()?.hide();
    } else if (guideId === StepGuideId.SaveEmail1) {
      this.saveEmail1Container()?.hide();
    } else if (guideId === StepGuideId.SaveEmail2) {
      this.step2Guide2Visible.set(false);
    } else if (guideId === StepGuideId.EntryActions1) {
      this.entryActionsGuideVisible.set(false);
      this.entryActions1Container()?.hide();
    }
  }

  private checkContainerForPopoverPosition(): void {
    const notInMaximizer = this.notInMaximizerDataResult;
    const containerHeight = this.readContainer().nativeElement.offsetHeight;

    if (notInMaximizer) {
      if (
        notInMaximizer.total > 3 ||
        (containerHeight < 500 && notInMaximizer.total > 1)
      ) {
        this.popoverPosition = 'top';
      }
    } else if (containerHeight < 450) {
      this.popoverPosition = 'top';
    }
  }

  populateAutoSaveStatusForCurrentPage(): void {
    const syncConfiguration = this.outlookStore.syncConfiguration();

    if (
      this.inMaximizer.length === 0 ||
      !syncConfiguration?.enabled ||
      !syncConfiguration.emailsSync.enabled
    )
      return;

    const startIndex = this.state.skip ?? 0;
    const endIndex = startIndex + (this.state.take ?? this.pageSize);
    const currentPageEntries = this.inMaximizer.slice(startIndex, endIndex);

    if (currentPageEntries.length === 0) return;

    this.loading.syncSubscriptions = true;

    from(currentPageEntries)
      .pipe(
        concatMap((entry) => {
          if (!entry.id) return of(null);

          const entryId = entry.id;

          if (this.autoSaveEnabledList.includes(entryId)) {
            return of(null);
          }

          if (
            syncConfiguration.emailsSync.enabled &&
            syncConfiguration.emailsSync.syncEverything
          ) {
            this.autoSaveEnabledList.push(entryId);
            return of(null);
          }

          return this.outlookSyncService
            .getEmailSubscriptionByKey(entryId, syncConfiguration.id)
            .pipe(
              map((subscription: EmailSubscription | undefined) => {
                if (subscription?.enabled) {
                  this.autoSaveEnabledList.push(entryId);
                }
              }),
              catchError(() => of(null)),
            );
        }),
        finalize(() => {
          this.loading.syncSubscriptions = false;
        }),
      )
      .subscribe();
  }

  private trackEmailSaveResult(success: boolean, entry: OutlookEmail): void {
    let entityType: EntryType | AbEntryType | EmailEntryType;

    if (entry.type === EntryType.AbEntry && entry.abentryType) {
      entityType = entry.abentryType;
    } else {
      entityType = entry.type ?? EntryType.Contact;
    }

    this.tracker.trackEvent({
      module: EventModule.Email,
      journey: EventJourney.Save,
      action: EventAction.Completed,
      metadata: {
        result: success ? EventMetadata.Success : EventMetadata.Failed,
        entity: entityType,
        emailDetails: false,
        search: this.isSavingFromPopup,
      },
    });
  }

  private handleError(error: unknown, context: string): void {
    const isError = error instanceof Error;
    const exception = isError ? error : new Error('Unknown error');

    this.insightsService.trackException(
      { exception },
      {
        context,
        message: isError
          ? `${context}: ${exception.message}`
          : `${context}: Non-error thrown: ${JSON.stringify(error)}`,
      },
    );
  }

  private handleLastContactDate(entry: OutlookEmail): void {
    const emailCreationDate = this.outlookService.mailboxItem?.dateTimeCreated;
    if (!emailCreationDate || !entry.id) return;

    const entryId = entry.id;
    this.loading.saveEmail = true;

    this.abentryService
      .updateLastContactDateWithPreferences(
        entryId,
        EntryType.AbEntry,
        AbEntryLastContactDatePreference.EmailSentFromOutlook,
        emailCreationDate,
        entry.abentryType,
        entry.parentKey,
      )
      .pipe(
        finalize(() => (this.loading.saveEmail = false)),
        catchError((error) => {
          console.error('Error updating last contact date:', error);
          return of(null);
        }),
      )
      .subscribe();
  }

  onAddEntry(event: AddEntryEvent): void {
    this.navigateToAdd(event.event, event.email, event.name);
  }

  onNavigateEntry(entry: OutlookEmail): void {
    if (!entry.id || !entry.type) return;
    this.navigateToEntry(entry.id, entry.type);
  }

  onSaveEntry(payload: { entry: OutlookEmail; fromPopup: boolean }): void {
    this.saveEmail(payload.entry, payload.fromPopup);
  }
}
